import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { HERO, SECOND_SCROLL } from '../../data/content';
import { LineReveal } from './Reveal';
import { JoinForm } from './JoinForm';

const EASE = [0.22, 1, 0.36, 1];

const FloatImage = ({ src, className, range = 60, delay = 0 }) => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 900], [0, range]);
  return (
    <motion.div
      className={className}
      style={{ y }}
      initial={{ opacity: 0, scale: 1.06 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.0, ease: EASE, delay }}
    >
      <img src={src} alt="" className="editorial-img w-full h-full object-cover" />
    </motion.div>
  );
};

const FRAME =
  'absolute z-0 overflow-hidden shadow-[0_28px_70px_-30px_rgba(4,37,42,0.45)] ring-1 ring-teal/5';

// Art-directed grid: wide + square on top, two tall anchors below — all held in
// the upper-right field, clear of the display type and the form.
const COLLAGE_XL = [
  { src: '/assets/h3.webp', cls: 'right-[18%] top-[7%] w-[216px] h-[150px]', range: 44, delay: 0.2 },
  { src: '/assets/h1.jpg', cls: 'right-[4%] top-[6%] w-[172px] h-[132px]', range: 30, delay: 0.28 },
  { src: '/assets/h2.jpg', cls: 'right-[24%] top-[28%] w-[150px] h-[224px]', range: -48, delay: 0.42 },
  { src: '/assets/h4.jpg', cls: 'right-[4%] top-[25%] w-[214px] h-[286px]', range: -26, delay: 0.5 },
];

// Reduced far-right column for lg (keeps clear of the display type).
const COLLAGE_LG = [
  { src: '/assets/h4.jpg', cls: 'right-[4%] top-[14%] w-[150px] h-[200px]', range: -30, delay: 0.3 },
  { src: '/assets/h2.jpg', cls: 'right-[7%] top-[52%] w-[120px] h-[180px]', range: 52, delay: 0.4 },
];

export const Hero = () => {
  const ref = useRef(null);

  return (
    <section id="top" ref={ref} className="relative pt-24 md:pt-28 pb-10 md:pb-14 overflow-hidden">
      {/* Art-directed editorial image collage in the right field */}
      {COLLAGE_XL.map((it, k) => (
        <FloatImage
          key={`xl-${k}`}
          src={it.src}
          range={it.range}
          delay={it.delay}
          className={`hidden xl:block ${FRAME} ${it.cls}`}
        />
      ))}
      {COLLAGE_LG.map((it, k) => (
        <FloatImage
          key={`lg-${k}`}
          src={it.src}
          range={it.range}
          delay={it.delay}
          className={`hidden lg:block xl:hidden ${FRAME} ${it.cls}`}
        />
      ))}

      <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-y-6">
          {/* Headline */}
          <div className="col-span-12 lg:col-span-11">
            <h1 className="font-serif uppercase tracking-[-0.01em] text-teal text-[3.4rem] sm:text-7xl lg:text-8xl xl:text-[8.5rem]">
              <LineReveal
                lines={['THE RIGHT', 'PEOPLE', 'CHANGE', 'EVERYTHING.']}
                lineClassName="block leading-[1.06]"
                delay={0.25}
                stagger={0.09}
              />
            </h1>
          </div>

          {/* Manifesto lines */}
          <div className="col-span-12 md:col-span-6 lg:col-start-1 lg:col-span-5 self-start">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
              className="space-y-1.5 text-lg md:text-xl font-light text-ink/85 leading-snug"
            >
              {HERO.lines.map((line, i) => (
                <motion.p
                  key={i}
                  data-testid={`hero-line-${i}`}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 1 + i * 0.12 }}
                  className={i === HERO.lines.length - 1 ? 'text-teal pt-3 font-normal' : ''}
                >
                  {line}
                </motion.p>
              ))}
              <motion.p
                data-testid="hero-home-line"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 1 + HERO.lines.length * 0.12 + 0.1 }}
                className="pt-5 max-w-xl text-base md:text-lg text-ink/65 leading-relaxed"
              >
                {SECOND_SCROLL.body}
              </motion.p>
            </motion.div>
          </div>

          {/* Form */}
          <div id="enter" className="col-span-12 md:col-span-6 lg:col-start-7 lg:col-span-6 scroll-mt-24">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 1.3 }}
              className="lg:pl-8 lg:border-l border-teal/10"
            >
              <div className="mb-7 flex items-baseline justify-between border-b border-teal/15 pb-4">
                <span className="text-xs tracking-[0.28em] uppercase text-teal font-semibold">
                  {HERO.formHeading}
                </span>
                <span className="font-serif italic text-teal/40 text-lg">01</span>
              </div>
              <JoinForm />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
